import React from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { searchBook } from '../../utils/api/bookApi';
import { captureException } from '../../utils/sentry';
import { truncate } from '../../utils/stringUtil';
import useClickOutside from '../../hooks/useClickOutside';
import './index.less';
var SEARCH_DEBOUNCE_MS = 300;
var MIN_QUERY_LENGTH = 3;
var stripIsbn = function (query) { return query.replace(/[- ]/g, ''); };
var isIsbnQuery = function (query) { return /^\d{10}$|^\d{13}$/.test(stripIsbn(query)); };
var getSearchPath = function (query) { return "/sok?query=" + encodeURIComponent(query); };
var formatPrice = function (price) {
    if (price === null || price === undefined) {
        return '';
    }
    if (Number(price) === 0) {
        return 'Gis bort!';
    }
    return "fra " + Number(price).toLocaleString('nb-NO') + " kr";
};
var defaultNavigate = function (href) {
    window.location.href = href;
};
/**
 * The frontpage search bar.
 *
 * Submitting the form navigates to the search page (which is React-owned),
 * so the form works even without JavaScript. While typing, book
 * suggestions from the Kotlin autocomplete endpoint are shown in a
 * dropdown, mirroring the legacy jQuery autocomplete.
 * */
var FrontpageSearch = function (_a) {
    var _b = _a.navigate, navigate = _b === void 0 ? defaultNavigate : _b;
    var _c = React.useState(''), query = _c[0], setQuery = _c[1];
    var _d = React.useState([]), results = _d[0], setResults = _d[1];
    var _e = React.useState(false), isOpen = _e[0], setIsOpen = _e[1];
    var _f = React.useState(null), activeIndex = _f[0], setActiveIndex = _f[1];
    var rootRef = React.useRef(null);
    var requestSeqRef = React.useRef(0);
    var closeDropdown = React.useCallback(function () {
        setIsOpen(false);
        setActiveIndex(null);
    }, []);
    useClickOutside(rootRef, closeDropdown);
    var debouncedSearchRef = React.useRef(debounce(function (nextQuery) {
        requestSeqRef.current += 1;
        var requestSeq = requestSeqRef.current;
        searchBook(nextQuery)
            .then(function (nextResults) {
            // The query may have changed while we were waiting for the
            // response, so only the newest request may update the state.
            if (requestSeq !== requestSeqRef.current) {
                return;
            }
            var normalizedQuery = stripIsbn(nextQuery);
            var exactMatch = nextResults.find(function (result) { return "" + result.isbn === normalizedQuery; });
            // Typing a full ISBN is an explicit book lookup, so an exact
            // match takes the user straight to the book page.
            if (isIsbnQuery(nextQuery) && exactMatch) {
                navigate("/bok/" + exactMatch.isbn);
                return;
            }
            setResults(nextResults);
            setActiveIndex(null);
            setIsOpen(true);
        })
            .catch(function (error) { return captureException(error, null, { 'search.query': nextQuery }); });
    }, SEARCH_DEBOUNCE_MS));
    React.useEffect(function () {
        var debouncedSearch = debouncedSearchRef.current;
        return function () { return debouncedSearch.cancel(); };
    }, []);
    var handleQueryChange = function (event) {
        var nextQuery = event.target.value;
        setQuery(nextQuery);
        if (nextQuery.trim().length < MIN_QUERY_LENGTH) {
            requestSeqRef.current += 1;
            setResults([]);
            closeDropdown();
            return;
        }
        debouncedSearchRef.current(nextQuery.trim());
    };
    var goToSearch = function (searchTerm) {
        closeDropdown();
        navigate(getSearchPath(searchTerm));
    };
    var selectResult = function (book) {
        closeDropdown();
        navigate(getSearchPath(book.title));
    };
    var handleKeyDown = function (event) {
        if (!isOpen || results.length === 0) {
            return;
        }
        var optionCount = results.length + 1; // Results + the "show more" row
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setActiveIndex(function (current) { return (current === null ? 0 : (current + 1) % optionCount); });
                break;
            case 'ArrowUp':
                event.preventDefault();
                setActiveIndex(function (current) { return (current === null ? optionCount - 1 : (current - 1 + optionCount) % optionCount); });
                break;
            case 'Enter':
                if (activeIndex === null) {
                    break;
                }
                event.preventDefault();
                if (activeIndex === results.length) {
                    goToSearch(query);
                }
                else {
                    selectResult(results[activeIndex]);
                }
                break;
            case 'Escape':
                closeDropdown();
                break;
            default:
                break;
        }
    };
    var isResultActive = function (index) { return activeIndex === index; };
    var isShowMoreActive = function () { return activeIndex === results.length; };
    var resultClassName = function (isActive) { return classNames('frontpage-search__result', { 'frontpage-search__result--active': isActive }); };
    return React.createElement("div", { className: "frontpage-search", ref: rootRef },
        React.createElement("form", { action: "/sok", method: "get", className: "search", role: "search" },
            React.createElement("input", { type: "search", name: "query", className: "autocomplete", placeholder: "S\u00F8k etter bok, fag eller ISBN", autoComplete: "off", autoFocus: true, "aria-label": "S\u00F8k etter bok, fag eller ISBN", role: "combobox", "aria-autocomplete": "list", "aria-expanded": isOpen, "aria-controls": "frontpage-search-results", value: query, onChange: handleQueryChange, onKeyDown: handleKeyDown, onFocus: function () { return results.length > 0 && setIsOpen(true); } }),
            React.createElement("button", { type: "submit", title: "S\u00F8k etter bok" },
                React.createElement("img", { src: "/static/img/search.svg", alt: "search" }))),
        isOpen && results.length > 0 && (React.createElement("ul", { className: "frontpage-search__results", id: "frontpage-search-results", role: "listbox", "aria-label": "S\u00F8keforslag" },
            results.map(function (book, index) { return (React.createElement("li", { key: book.isbn, role: "option", "aria-selected": isResultActive(index), className: resultClassName(isResultActive(index)) },
                React.createElement("button", { type: "button", className: "frontpage-search__result-button", onClick: function () { return selectResult(book); }, onMouseEnter: function () { return setActiveIndex(index); } },
                    React.createElement("span", { className: "frontpage-search__result-title" }, truncate(book.title, 47)),
                    React.createElement("span", { className: "frontpage-search__result-metadata" },
                        book.authors.length > 0 && (React.createElement("span", { className: "frontpage-search__result-authors" }, truncate(book.authors.join(', '), 60))),
                        book.lowestPrice !== null && book.lowestPrice !== undefined && (React.createElement("span", { className: "frontpage-search__result-price" }, formatPrice(book.lowestPrice))))))); }),
            React.createElement("li", { role: "option", "aria-selected": isShowMoreActive(), className: resultClassName(isShowMoreActive()) },
                React.createElement("button", { type: "button", className: "frontpage-search__result-button frontpage-search__show-more", onClick: function () { return goToSearch(query); }, onMouseEnter: function () { return setActiveIndex(results.length); } }, "Vis flere treff...")))));
};
export default FrontpageSearch;
